import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Zap, CheckCircle2, ShieldCheck, Loader2 } from "lucide-react";

export default function Activate() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();
  const { setSession } = useAuth();
  const [state, setState] = useState("checking"); // checking | valid | invalid | done
  const [info, setInfo] = useState(null);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    api.post("/auth/activate/verify", { token })
      .then((r) => { setInfo(r.data); setState("valid"); })
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (pw !== confirm) { setError("Passwords do not match."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/auth/activate", { token, password: pw });
      setSession(data.token, data.user);
      setState("done");
      setTimeout(() => nav("/"), 900);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Activation failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-slate-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 neec-grid-bg opacity-[0.07]" />
        <div className="relative flex items-center gap-2.5">
          <div className="w-11 h-11 rounded-xl bg-emerald-500 flex items-center justify-center font-head font-extrabold text-xl">N</div>
          <div>
            <p className="font-head font-extrabold text-xl leading-none">NEEC POS</p>
            <p className="text-xs text-emerald-400 tracking-wider flex items-center gap-1"><Zap className="w-3 h-3" /> BUSINESS PLATFORM</p>
          </div>
        </div>
        <div className="relative space-y-4">
          <h1 className="font-head text-4xl font-extrabold leading-tight">Welcome to<br /><span className="text-emerald-400">NEEC POS</span></h1>
          <p className="text-slate-300 max-w-md">You choose your own password. No one at NEEC can ever see it. That's how we keep your business secure.</p>
        </div>
        <p className="relative text-xs text-slate-500">© 2026 NEEC POS</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm" data-testid="activate-panel">
          {state === "checking" && (
            <div className="flex flex-col items-center text-muted-foreground gap-3 py-20">
              <Loader2 className="w-8 h-8 animate-spin" /> Checking your invitation…
            </div>
          )}
          {state === "invalid" && (
            <div className="text-center py-16">
              <div className="w-14 h-14 rounded-2xl bg-rose-500/10 text-rose-500 flex items-center justify-center mx-auto mb-4"><ShieldCheck className="w-7 h-7" /></div>
              <h2 className="font-head text-2xl font-extrabold">Link expired or invalid</h2>
              <p className="text-muted-foreground mt-2 mb-6">This activation link is no longer valid. Please ask NEEC to resend your invitation.</p>
              <Button onClick={() => nav("/login")} variant="outline" data-testid="activate-to-login">Go to Login</Button>
            </div>
          )}
          {state === "done" && (
            <div className="text-center py-16" data-testid="activate-success">
              <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="w-7 h-7" /></div>
              <h2 className="font-head text-2xl font-extrabold">Account activated!</h2>
              <p className="text-muted-foreground mt-2">Taking you to your dashboard…</p>
            </div>
          )}
          {state === "valid" && (
            <>
              <div className="lg:hidden flex items-center gap-2.5 mb-8">
                <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center text-white font-head font-extrabold">N</div>
                <p className="font-head font-extrabold text-lg">NEEC POS</p>
              </div>
              <h2 className="font-head text-3xl font-extrabold">Activate your account</h2>
              <p className="text-muted-foreground mt-1 mb-1">for <span className="font-semibold text-foreground">{info?.business_name}</span></p>
              <p className="text-xs text-muted-foreground mb-6">Login email: <span className="mono">{info?.email}</span></p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider">Create Password</Label>
                  <Input value={pw} onChange={(e) => setPw(e.target.value)} type="password" className="h-12 mt-1.5" placeholder="At least 8 characters" data-testid="activate-password" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider">Confirm Password</Label>
                  <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} type="password" className="h-12 mt-1.5" data-testid="activate-confirm" />
                </div>
                {error && <p className="text-sm text-rose-500" data-testid="activate-error">{error}</p>}
                <Button type="submit" disabled={saving} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white text-base font-semibold" data-testid="activate-submit">
                  {saving ? "Activating…" : "Activate My Account"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
